import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';

function Admin() {
  const [digitalProducts, setDigitalProducts] = useState([]);
  const [squareProducts, setSquareProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ squareProductId: '', downloadLink: '' });
  const [editingProduct, setEditingProduct] = useState(null);
  const [storeInfo, setStoreInfo] = useState({ name: '', address: '', shippingCost: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch('/api/admin/store-info', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => setStoreInfo(data));
  }, [token]);

  useEffect(() => {
    fetch('/api/admin/digital-products', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => setDigitalProducts(Array.isArray(data) ? data : []));
  }, [token]);

  useEffect(() => {
    fetch('/api/admin/square-products', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
      .then(res => res.json())
      .then(data => setSquareProducts(Array.isArray(data) ? data : []));
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleAddProduct = async () => {
    if (!selectedFile) {
      setUploadStatus('Please select a file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('ebook', selectedFile);

    try {
      setUploadStatus('Uploading...');
      const uploadRes = await fetch('/api/admin/ebooks', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error('File upload failed');
      }

      const uploadData = await uploadRes.json();
      const productData = { ...newProduct, downloadLink: uploadData.downloadUrl };

      const productRes = await fetch('/api/admin/digital-products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(productData),
      });

      if (!productRes.ok) {
        throw new Error('Failed to create digital product');
      }

      const newDigitalProduct = await productRes.json();
      setDigitalProducts([...digitalProducts, newDigitalProduct]);
      setNewProduct({ squareProductId: '', downloadLink: '' });
      setSelectedFile(null);
      setUploadStatus('Product added successfully!');
    } catch (error) {
      setUploadStatus(error.message);
    }
  };

  const handleDeleteProduct = (id) => {
    fetch(`/api/admin/digital-products/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }).then(() => {
      setDigitalProducts(digitalProducts.filter(p => p.id !== id));
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
  };

  const handleUpdateProduct = () => {
    fetch(`/api/admin/digital-products/${editingProduct.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ downloadLink: editingProduct.download_link }),
    })
      .then(res => res.json())
      .then(data => {
        setDigitalProducts(digitalProducts.map(p => (p.id === data.id ? data : p)));
        setEditingProduct(null);
      });
  };

  const handleStoreInfoChange = (e) => {
    const { name, value } = e.target;
    setStoreInfo({ ...storeInfo, [name]: value });
  };

  const handleUpdateStoreInfo = () => {
    fetch('/api/admin/store-info', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(storeInfo),
    })
      .then(res => res.json())
      .then(data => setStoreInfo(data));
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Admin
      </Typography>
      <Paper style={{ marginBottom: '20px', padding: '20px' }}>
        <Typography variant="h6">Store Information</Typography>
        <TextField
          label="Store Name"
          name="name"
          value={storeInfo.name}
          onChange={handleStoreInfoChange}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Pickup Address"
          name="address"
          value={storeInfo.address}
          onChange={handleStoreInfoChange}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Shipping Cost"
          name="shippingCost"
          type="decimal"
          value={storeInfo.shippingCost}
          onChange={handleStoreInfoChange}
          fullWidth
          margin="normal"
        />
        <Button variant="contained" color="primary" onClick={handleUpdateStoreInfo}>
          Update Store Info
        </Button>
      </Paper>
      <Paper style={{ marginBottom: '20px', padding: '20px' }}>
        <Typography variant="h6">Add New Digital Product</Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel id="square-product-id-label">Square Product</InputLabel>
          <Select
            labelId="square-product-id-label"
            id="square-product-id"
            value={newProduct.squareProductId}
            onChange={handleInputChange}
            name="squareProductId"
          >
            {squareProducts.flatMap(product =>
                (product.itemData.variations || []).map(variation => (
                    <MenuItem key={variation.id} value={variation.id}>
                        {product.itemData.name} - {variation.itemVariationData.name}
                    </MenuItem>
                ))
            )}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          component="label"
          fullWidth
          style={{ margin: '8px 0' }}
        >
          Upload Ebook
          <input
            type="file"
            hidden
            onChange={handleFileChange}
          />
        </Button>
        {selectedFile && <Typography>{selectedFile.name}</Typography>}
        {uploadStatus && <Typography color={uploadStatus.includes('successfully') ? 'primary' : 'error'}>{uploadStatus}</Typography>}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddProduct}
          disabled={!newProduct.squareProductId || !selectedFile}
          style={{ marginTop: '8px' }}
        >
          Add Product
        </Button>
      </Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Square Product ID</TableCell>
            <TableCell>Download Link</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {digitalProducts.map(product => (
            <TableRow key={product.id}>
              <TableCell>{product.square_product_id}</TableCell>
              <TableCell>
                {editingProduct && editingProduct.id === product.id ? (
                  <TextField
                    value={editingProduct.download_link}
                    onChange={(e) =>
                      setEditingProduct({ ...editingProduct, download_link: e.target.value })
                    }
                    inputProps={{ 'data-testid': 'edit-input' }}
                  />
                ) : (
                  product.download_link
                )}
              </TableCell>
              <TableCell>
                {editingProduct && editingProduct.id === product.id ? (
                  <Button color="primary" onClick={handleUpdateProduct}>
                    Save
                  </Button>
                ) : (
                  <Button color="primary" onClick={() => handleEditProduct(product)}>
                    Edit
                  </Button>
                )}
                <Button color="secondary" onClick={() => handleDeleteProduct(product.id)}>
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Container>
  );
}

export default Admin;
